import React, { useState, useContext, useRef } from 'react';
import RoomContext from '../RoomContext';
import { useRoomApi } from '../hooks/useRoomApi';

// Editable per-room control config. Each change is auto-saved (debounced) — no save button — matching
// CalibrationInput's pattern. Numeric fields are step-typed (integers vs. decimals); the whole config
// object is PUT back on save (the backend ignores the read-only roomId).
const FIELDS = [
  { key: 'standByTemperature', label: 'Temp. de reposo (°C)', step: 0.5 },
  { key: 'defaultActiveTemperature', label: 'Temp. activa por defecto (°C)', step: 0.5 },
  { key: 'temperatureRangeTolerance', label: 'Tolerancia de rango (°C)', step: 0.1 },
  { key: 'degreesPerHourHeatingSpeed', label: 'Velocidad de calentamiento (°C/h)', step: 0.1 },
  { key: 'minTemperatureDeltaForPriming', label: 'Delta mínimo para cebado (°C)', step: 0.1 },
  { key: 'heaterResponseDelayInMinutes', label: 'Demora de respuesta (min)', step: 0.5 },
  { key: 'maxPreHeatTimeInSeconds', label: 'Precalentamiento máx. (s)', step: 1 },
  { key: 'activityStartPaddingInSeconds', label: 'Margen de inicio (s)', step: 1 },
  { key: 'impromptuDurationInSeconds', label: 'Duración evento temporal (s)', step: 1 },
];

//not tested yet
const CALENDAR_HINT = 
  'Para obtener este valor, abra Google Calendar, vaya a Configuración, ' +
  'seleccione el calendario deseado, ingrese a la sección "Integrar el calendario" ' +
  'y copie el valor de "ID de calendario".';

const RoomConfigForm = ({ config, onSaved }) => {
  const { tenant, room } = useContext(RoomContext);
  const api = useRoomApi(tenant, room);
  const [draft, setDraft] = useState(config);
  const [status, setStatus] = useState(null); // 'saving' | 'saved' | 'error'
  const [showCalendarHint, setShowCalendarHint] = useState(false);
  const timer = useRef(null);

  const save = async (next) => {
    setStatus('saving');
    try {
      await api.updateConfig(next);
      setStatus('saved');
      onSaved && onSaved();
    } catch (e) {
      setStatus('error');
    }
  };

  const setField = (key, value) => {
    const next = { ...draft, [key]: value };
    setDraft(next);
    setStatus(null);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => save(next), 400);
  };

  return (
    <>
      <div className="row g-2">
        {FIELDS.map(({ key, label, step }) => (
          <div className="col-6" key={key}>
            <label className="form-label small mb-1">{label}</label>
            <input
              type="number"
              step={step}
              className="form-control form-control-sm"
              value={draft[key] ?? ''}
              onChange={(e) => setField(key, e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>
        ))}
        <div className="col-12">
          <label className="form-label small mb-1 d-flex align-items-center gap-1">
            Calendario
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-secondary lh-1"
              style={{ fontSize: '0.75rem' }}
              onClick={() => setShowCalendarHint((v) => !v)}
              aria-label="Ayuda sobre ID de calendario"
            >
              &#9432;
            </button>
          </label>
          {showCalendarHint && (
            <div className="alert alert-info alert-sm py-1 px-2 mb-1 small" role="alert">
              {CALENDAR_HINT}
            </div>
          )}
          <input
            type="text"
            className="form-control form-control-sm"
            value={draft.calendarListName ?? ''}
            onChange={(e) => setField('calendarListName', e.target.value)}
          />
        </div>
      </div>
      <div className="small mt-2" style={{ minHeight: '1.2em' }}>
        {status === 'saving' && <span className="text-muted">Guardando…</span>}
        {status === 'saved' && <span className="text-success">Guardado</span>}
        {status === 'error' && <span className="text-danger">No se pudo guardar.</span>}
      </div>
    </>
  );
};

export default RoomConfigForm;
